import { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssistantConfig, type AssistantConfig } from '@ohpen/enso-ui';

import { useFeatureToggle } from '@pmp/hooks';
import { usePageContextStore } from '@pmp/stores';

import { toInAppPath } from './inAppLink';
import { pmpAssistantConfig } from './pmpAssistant';

/**
 * Renders no DOM; must sit inside PmpConfigProvider to read the feature toggles.
 *
 * No permission check on top of the flag: the agent authorizes its tools fail-closed
 * per caller, so a user only ever gets the tools they may use. Passing null to
 * `useAssistantConfig` keeps the assistant runtime and its antd-X chunk unloaded.
 */
export const AssistantGate = (): null => {
  const flagEnabled = useFeatureToggle(['enableAssistant']);
  const { pathname } = useLocation();
  const clearOtherRoutes = usePageContextStore(state => state.clearOtherRoutes);
  const navigate = useNavigate();

  // The panel needs one stable config, so the current navigate is reached through a ref
  // rather than rebuilding the config whenever the router hands out a new one.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const config = useMemo<AssistantConfig>(
    () => ({
      ...pmpAssistantConfig,
      content: {
        ...pmpAssistantConfig.content,
        onLinkClick: href => {
          const path = toInAppPath(href, window.location.origin);
          if (path === null) return false;
          navigateRef.current(path);
          return true;
        },
      },
    }),
    [],
  );

  useAssistantConfig(flagEnabled ? config : null);

  // Drops the page context of the route just left; scoped to "other routes" (not a blanket clear)
  // because the list on the route being entered has already reported itself by the time this runs.
  useEffect(() => clearOtherRoutes(pathname), [clearOtherRoutes, pathname]);

  return null;
};
